import React, { useCallback, useEffect, useRef, useState } from 'react';

import { deLocalizeFormula, localizeFormula, validateLocalizedFormula } from '../../../utils/formulas/i18n-utils';
import { FormulaError, FormulaErrorCode } from '../../../ui/data-builder/formula-content-form/types';
import { FormulaNonFinancialDriver } from '../../../core/api/types/non-financial-drivers';
import { OutputFrequency } from '../../../ui/data-builder/types';

export const useFormulaDriverForm = (driver?: FormulaNonFinancialDriver) => {
  const formulaString = useRef('');
  const formulaTouched = useRef(false);
  const latestOutputFrequency = useRef(OutputFrequency.monthly);
  const restoreFormulaString = useRef(''); // Only changes on new driver or on save. Needed to restore state should a save fail.

  const [formulaError, setFormulaError] = useState<FormulaError>();
  const [outputFrequency, setOutputFrequency] = useState(OutputFrequency.monthly);
  const [outputOnDay, setOutputOnDay] = useState('32');
  const [outputOnMonth, setOutputOnMonth] = useState('1');

  const getRequestData = useCallback(() => {
    return {
      expression: deLocalizeFormula(formulaString.current),
      output_frequency: outputFrequency,
      output_occurs_on_day: parseInt(outputOnDay, 10),
      output_occurs_on_month: parseInt(outputOnMonth, 10)
    };
  }, [outputOnDay, outputOnMonth, outputFrequency]);

  const validateFormula = useCallback(() => {
    const isValidLocalization = validateLocalizedFormula(formulaString.current);
    if (!isValidLocalization) {
      setFormulaError({
        error_code: FormulaErrorCode.ERROR_INVALID_SYNTAX,
        is_valid: false
      });
      return false;
    }

    const { IsValid } = window.validateFormula
      ? window.validateFormula(deLocalizeFormula(formulaString.current), latestOutputFrequency.current.toUpperCase())
      : { IsValid: true };

    if (!IsValid) {
      setFormulaError({
        error_code: FormulaErrorCode.ERROR_INVALID_SYNTAX,
        is_valid: false
      });
    } else {
      setFormulaError(undefined);
    }

    return IsValid;
  }, []);

  const revalidateFormula = useCallback(() => {
    if (formulaTouched.current) {
      validateFormula();
    }
  }, [validateFormula]);

  const updateRestoreFormulaString = useCallback(() => {
    restoreFormulaString.current = formulaString.current;
  }, []);

  // ************
  // * Handlers *
  // ************

  const handleFormulaChange = useCallback(
    (newFormulaString: string) => {
      if (newFormulaString.trim() !== formulaString.current.trim()) {
        formulaString.current = newFormulaString.trim();
        formulaTouched.current = !!formulaString.current || formulaTouched.current;
        revalidateFormula();
      }
    },
    [revalidateFormula]
  );

  const handleOutputFrequencyChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      latestOutputFrequency.current = e.target.value as OutputFrequency;
      setOutputFrequency(latestOutputFrequency.current);
      formulaTouched.current = !!formulaString.current || formulaTouched.current;
      revalidateFormula();
    },
    [revalidateFormula]
  );

  const handleOutputOnDayChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setOutputOnDay(e.target.value);
  }, []);

  const handleOutputOnMonthChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setOutputOnMonth(e.target.value);
  }, []);

  // ***********
  // * Effects *
  // ***********

  // If the driver data changes, set the form accordingly
  useEffect(() => {
    if (driver) {
      setOutputFrequency(driver.output_frequency as OutputFrequency);
      latestOutputFrequency.current = driver.output_frequency as OutputFrequency;
      setOutputOnDay(String(driver.output_occurs_on_day ?? '1'));
      setOutputOnMonth(String(driver.output_occurs_on_month ?? '32'));
      formulaString.current = localizeFormula(driver.expression);
      restoreFormulaString.current = formulaString.current;
    } else {
      setOutputFrequency(OutputFrequency.monthly);
      latestOutputFrequency.current = OutputFrequency.monthly;
      setOutputOnDay('32');
      setOutputOnMonth('1');
      formulaString.current = '';
      restoreFormulaString.current = '';
    }

    setFormulaError(undefined);
    formulaTouched.current = false;
  }, [driver]);

  return {
    formulaError,
    getRequestData,
    handleFormulaChange,
    handleOutputFrequencyChange,
    handleOutputOnDayChange,
    handleOutputOnMonthChange,
    restoreFormulaStringRef: restoreFormulaString,
    outputFrequency,
    outputOnDay,
    outputOnMonth,
    revalidateFormula,
    updateRestoreFormulaString,
    validateFormula
  };
};
